import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react';
import { z } from 'zod';

import { evaluationDetailsQueryOptions } from '@/api/evaluationDetails';
import { EvaluationForm } from '@/components/evaluation/evaluationForm';
import { Button } from '@/components/input/button';
import { useEvaluationTargets } from '@/hooks/useEvaluationTargets';
import { EvaluationIdSchema } from '@/schemas/evaluationId';
import { EvaluationSearchSchema } from '@/schemas/evaluationSearch';
import { findTargetByEvaluationId, targetHeaderOf } from '@/util/evaluationTargets';
import { isNotFoundError } from '@/util/httpError';
import { deadlineOf } from '@/util/todoItems';


export const Route = createFileRoute('/dashboard/_authenticated/evaluation/$evaluationId')({
  component: RouteComponent,
  staticData: { pageWidth: 'wide' },
  params: {
    parse: (parameters) => z.object({ evaluationId: z.preprocess(Number, EvaluationIdSchema) }).parse(parameters)
  },
  validateSearch: EvaluationSearchSchema,
  errorComponent: ({ error }) => (
    <div>Invalid route params: {error.message}</div>
  ),
})

function RouteComponent() {
  const match = Route.useMatch();
  const { evaluationId } = Route.useParams();
  const { form } = Route.useSearch();
  const evaluations = useEvaluationTargets();
  const [now] = useState(() => new Date());
  const target = findTargetByEvaluationId(evaluations.targets, evaluationId);
  // Full details by id (categories live in `evaluation.commentList`), never the sidebar menu list:
  // that one only carries OPEN evaluations.
  const { data, error, isLoading, refetch } = useQuery(evaluationDetailsQueryOptions(evaluationId));

  if (isLoading) {
    return <div className="text-body2 text-neutral-600">Loading...</div>;
  }

  if (isNotFoundError(error)) {
    return (
      <div className="flex flex-col items-start gap-3">
        <div className="text-body2 text-error-300">Evaluation not found</div>
        <Link className="text-body2 text-primary-default underline" to="/dashboard">Back to Home</Link>
      </div>
    );
  }

  // No data without an error = the response didn't match the schema (production `parseSchema`).
  if (error || !data) {
    return (
      <div className="flex flex-col items-start gap-3">
        <div className="text-body2 text-error-300">Couldn't load this evaluation</div>
        <Button onClick={() => void refetch()} size="sm" type="button">Retry</Button>
      </div>
    );
  }

  const { campaign, employee, evaluation } = data;
  // Header from the person's targets entry (role, shared groups); a link to an evaluation that is no
  // longer open keeps the evaluation's own data. The campaign is always the evaluation's own: several
  // can be open at once.
  const header = target ? targetHeaderOf(target, campaign) : {
    breadcrumbLabel: evaluation.evaluatee.name,
    title: `Evaluate ${evaluation.evaluatee.name}`,
    subtitle: employee.role ? `${employee.role} · ${campaign.name}` : campaign.name,
  };

  return (
    <EvaluationForm
      breadcrumbLabel={header.breadcrumbLabel}
      deadline={deadlineOf(campaign.endsAt, now)}
      breadcrumbPath={match.pathname}
      campaignName={campaign.name}
      evaluationId={evaluation.id}
      headerAvatar={{
        name: evaluation.evaluatee.name,
        pictureBase64: evaluation.evaluatee.picture,
      }}
      headerSubtitle={header.subtitle}
      headerTitle={header.title}
      initialFormId={form}
      items={evaluation.commentList}
      key={evaluation.id}
      readOnly={evaluation.status !== "OPEN" || campaign.isLocked}
    />
  );
}
